/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import DotGrid from "@/components/customCopmonent/DotGrid";
import { PageHeaderHero, PAGE_HERO_CLASS } from "@/components/customCopmonent/Text/PageHeaderHero";
import { SectionWrapper } from "../Clientele.page-primitives";
import { RevealOnScroll } from "@/components/SpecialProjectsPage.primitives";

import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom"; // âœ… NEW
import { useTranslation } from "react-i18next";
import ProductCard from "@/components/customCopmonent/ProductCard";
import Seo from "@/components/Seo";
import AcheShowcase, {
	type ShowcaseHighlight,
	type ShowcaseSpec,
} from "@/components/customCopmonent/AcheShowcase";
import {
	ShieldCheck,
	Layers,
	Wind,
	Gauge,
	Waves,
	Boxes,
	Fan,
} from "lucide-react";

// -----------------------------
// âœ… NEW: Industry Config
// -----------------------------
const INDUSTRIES = ["Power Generation", "HVAC Systems", "Oil & Gas"];

// -----------------------------
// Showcase content for ACHE
// -----------------------------
const ACHE_IMAGES: string[] = [
	"/images/finalProducts/AIR COOLED HEAT EXCHANGER-HIGH PRESSURE DESIGN.png",
	"/images/finalProducts/UNIT COOLER (OFAF).png",
	"/images/finalProducts/PLUG TYPE - 2.png",
];

// Icon order matches the highlight/spec keys resolved via i18n inside the component.
const ACHE_HIGHLIGHT_ICONS = [ShieldCheck, Layers, Wind];
const ACHE_SPEC_ICONS = [Wind, Gauge, Waves, Boxes, Fan];

// slug â†’ label mapping
const INDUSTRY_SLUG_MAP: Record<string, string> = {
	"power-generation": "Power Generation",
	"oil-gas": "Oil & Gas",
	"marine-offshore": "Marine & Offshore",
	"water-treatment": "Water Treatment",
	"plastic-injection": "Plastic Injection Molding & Machine Tools",
	hvac: "HVAC Systems",
	"chemical-processing": "Chemical Processing",
	"data-center": "Data Center",
	"steel-metallurgy": "Steel & Metallurgy",
	"renewable-energy": "Renewable Energy",
	"process-industries": "Process Industries (Pulp, Paper & Sugar)",
};

// reverse mapping
const getSlugFromIndustry = (industry: string) => {
	return Object.entries(INDUSTRY_SLUG_MAP).find(
		([, value]) => value === industry,
	)?.[0];
};

const AirCooledHeatExchangers = () => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// -----------------------------
	// âœ… NEW: State
	// -----------------------------
	const [activeIndustry, setActiveIndustry] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	// -----------------------------
	// âœ… NEW: Debounce Logic
	// -----------------------------
	useEffect(() => {
		const t = setTimeout(() => {
			setDebouncedSearch(search.toLowerCase());
		}, 300);
		return () => clearTimeout(t);
	}, [search]);

	// -----------------------------
	// âœ… NEW: Read URL param on load
	// -----------------------------
	useEffect(() => {
		const industrySlug = searchParams.get("industry");
		if (industrySlug && INDUSTRY_SLUG_MAP[industrySlug]) {
			setActiveIndustry(INDUSTRY_SLUG_MAP[industrySlug]);
		}
	}, []);

	// -----------------------------
	// Page Data (UNCHANGED)
	// -----------------------------
	const pageData = {
		title: t("productPages.airCooled.hero.title"),
		bgimage: "/images/header/AIRCOOLEDHEATEXCHANGER.jpg",

		description: t("productPages.airCooled.hero.tagline"),

		meta: t("productPages.airCooled.hero.meta", {
			returnObjects: true,
		}) as string[],

		projects: [
			{
				id: "10",
				title: "Oil Cooler 150 kW",
				application: "Oil Cooler",
				capacity: "150 kW",
				image: "/images/products3D/CPRI.png",
				details: {
					Tube: "SS 304",
					Fins: "Aluminium",
					"Fan Make": "Zeihl Abegg",
					Tubesheet: "Carbon Steel",
					Other: "Carbon Steel",
				},
				aplication: ["Oil & Gas", "HVAC Systems", "Power Generation"],
			},
			{
				id: "11",
				title: "Power Sector Oil Cooler 125 kW",
				application: "Oil Cooler",
				capacity: "125 kW",
				image: "/images/products3D/Power Sector.png",
				details: {
					Industry: "Power Sector",
					Tube: "Aluminium",
					Fins: "Aluminium",
					"Fan Make": "Zeihl Abegg (2W + 1S)",
					Tubesheet: "Carbon Steel",
					Other: "Carbon Steel",
				},
				aplication: ["Oil & Gas", "HVAC Systems", "Power Generation"],
			},
			{
				id: "12",
				title: "Power Sector Oil Cooler 200 kW",
				application: "Oil Cooler",
				capacity: "200 kW",
				image: "/images/products3D/Power SectorBIG.png",
				details: {
					Industry: "Power Sector",
					Tube: "Aluminium",
					Fins: "Aluminium",
					"Fan Make": "Zeihl Abegg (3 fans working + 1 Standby)",
					Tubesheet: "Carbon Steel",
					Other: "Carbon Steel",
				},
				aplication: ["Oil & Gas", "HVAC Systems", "Power Generation"],
			},

			{
				id: "8",
				title: "Oil Cooler 105 kW ,200 LPM",
				application: "Oil Cooler",
				capacity: "105 kW",
				image: "/images/products3D/Petronas.png",
				details: {
					Industry: "Oil & Gas",
					Core: "Plate Finned Type",
					Tube: "SS 304",
					Other: "Carbon Steel",
					Fan: "GRP",
					"Oil Flow Rate": "200 LPM",
				},
				aplication: ["Oil & Gas", "HVAC Systems", "Power Generation"],
			},
			{
				id: "9",
				title: "Oil Cooler 500 kW",
				application: "Oil Cooler",
				capacity: "500 kW",
				image: "/images/products3D/IOCL.png",
				details: {
					Industry: "Oil & Gas",
					"Design Standard": "API 661",
					Core: "Plate Finned Type",
					Tube: "Carbon Steel (Galvanised)",
					Other: "Carbon Steel",
					Fan: "GRP",
					Weight: "8000 Kg",
				},
				aplication: ["Oil & Gas", "HVAC Systems", "Power Generation"],
			},
			{
				id: "13",
				title: "Air Blast Cooler 12 kW",
				application: "Air Blast Cooler",
				capacity: "12 kW",
				image: "/images/products3D/Marjan.png",
				details: {
					"Design Standard": "API 661 & Saudi Aramco Specification",
					Tube: "Duplex Stainless Steel",
					Header: "Duplex Stainless Steel",
					Motor: "As per Saudi Aramco AVL",
				},
				aplication: ["Oil & Gas"],
			},
			{
				id: "14",
				title: "Air Blast Cooler 2 kW",
				application: "Air Blast Cooler",
				capacity: "2 kW",
				image: "/images/products3D/PDO.png",
				details: {
					"Design Standard": "API 661",
					Tube: "Stainless Steel 316L",
					Header: "Stainless Steel 316L",
					Motor: "As per PDO Oman AVL",
				},
				aplication: ["Oil & Gas"],
			},
			{
				id: "15",
				title: "Air Cooled Combi Cooler (Oil & Water)",
				application: "Air Cooled Combi Cooler (Oil & Water)",
				capacity: "",
				image: "/images/products3D/ADNOC.png",
				details: {
					Industry: "Oil & Gas",
					"Design Standard":
						"API 661, ADNOC Specifications, ASME Sec VIII Div.1",
					Certification: "ASME U Stamp",
					Tube: "Stainless Steel 316L",
					Header: "Stainless Steel 316L",
					"Structural Material": "Carbon Steel",
					Painting: "C5M with NACE inspection",
				},
				aplication: ["Oil & Gas"],
			},
			{
				id: "16",
				title: "Air Cooled Oil Cooler",
				application: "Air Cooled Oil Cooler",
				capacity: "",
				image: "/images/products3D/Zuluf.png",
				details: {
					Industry: "Oil & Gas",
					"Design Standard": "ASME Sec VIII Div. 2",
					Certification: "ASME U2 Stamp",
					Tube: "Stainless Steel 316L",
					Header: "Stainless Steel 316L",
					"Structural Material": "Carbon Steel",
					Painting: "C5M",
				},
				aplication: ["Oil & Gas"],
			},
			{
				id: "17",
				title: "Air Cooled Combi Cooler,Stainless Steel 316L",
				application:
					"Air Cooled Combi Cooler (Nitrogen Intercooler Aftercooler Water)",
				capacity: "",
				image: "/images/products3D/ADNOC â€“ Hail.png",
				details: {
					Industry: "Oil & Gas",
					"Design Standard": "ASME Sec VIII Div.1",
					Certification: "ASME U Stamp",
					Tube: "Stainless Steel 316L",
					Header: "Stainless Steel 316L",
					"Structural Material": "Carbon Steel",
					Painting: "C5M with NACE inspection",
				},
				aplication: ["Oil & Gas"],
			},
		],
	};

	// -----------------------------
	// âœ… NEW: Filter + Search + Sort
	// -----------------------------
	const filteredProjects = useMemo(() => {
		let data = [...pageData.projects];

		// SEARCH
		if (debouncedSearch) {
			data = data.filter((item) => {
				const text = (
					item.title +
					" " +
					(item.application || "")
				).toLowerCase();

				const industries = (item.aplication || []).join(" ").toLowerCase();

				return (
					text.includes(debouncedSearch) || industries.includes(debouncedSearch)
				);
			});
		}

		// FILTER
		if (activeIndustry) {
			data = data.filter((item) =>
				(item.aplication || []).includes(activeIndustry),
			);
		}

		// SORT
		if (activeIndustry) {
			data.sort((a, b) => {
				const aMatch = (a.aplication || []).includes(activeIndustry) ? 1 : 0;
				const bMatch = (b.aplication || []).includes(activeIndustry) ? 1 : 0;
				return bMatch - aMatch;
			});
		}

		return data;
	}, [debouncedSearch, activeIndustry]);

	// -----------------------------
	// âœ… NEW: Handle Filter Click
	// -----------------------------
	const handleFilterClick = (industry: string) => {
		const isSame = activeIndustry === industry;

		if (isSame) {
			setActiveIndustry(null);
			setSearchParams({});
		} else {
			setActiveIndustry(industry);
			const slug = getSlugFromIndustry(industry);
			setSearchParams({ industry: slug || "" });
		}
	};

	// Localized showcase content (icons stay static; copy resolves via i18n).
	const highlightKeys = [
		"designStandards",
		"surfaceOptimization",
		"airflowControl",
	] as const;
	const specKeys = [
		"coolingMedia",
		"designPressure",
		"tubePatterns",
		"headerTypes",
		"fanConfiguration",
	] as const;

	const ACHE_HIGHLIGHTS: ShowcaseHighlight[] = highlightKeys.map((k, i) => ({
		icon: ACHE_HIGHLIGHT_ICONS[i],
		title: t(`productPages.airCooled.showcase.highlights.${k}.title`),
		body: t(`productPages.airCooled.showcase.highlights.${k}.body`),
	}));

	const ACHE_SPECS: ShowcaseSpec[] = specKeys.map((k, i) => ({
		icon: ACHE_SPEC_ICONS[i],
		feature: t(`productPages.airCooled.showcase.specs.${k}.feature`),
		capability: t(`productPages.airCooled.showcase.specs.${k}.capability`),
	}));

	return (
		<>
			<Seo
				titleKey="productPages.airCooled.seo.title"
				descriptionKey="productPages.airCooled.seo.description"
				path="/products/air-cooled-heat-exchangers"
			/>
			<Navigation />

			{/* HERO */}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${pageData.bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
					/>
				</div>

				<PageHeaderHero
					title={pageData.title}
					description={pageData.description}
					meta={pageData.meta}
				/>
			</SectionWrapper>

			{/* PRODUCTS */}
			{/* <SectionWrapper className="pt-0">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
						{filteredProjects.map((project, index) => (
							<RevealOnScroll
								key={project.id}
								delay={index * 0.1}>
								<ProductCard
									product={project}
									index={index}
								/>
							</RevealOnScroll>
						))}
					</div>

					{filteredProjects.length === 0 && (
						<p className="text-center text-gray-500 mt-10">
							No matching projects found.
						</p>
					)}
				</div>
			</SectionWrapper> */}

			{/* ADD HERE */}
			<AcheShowcase
				title={
					<>
						{t("productPages.airCooled.showcase.titleMain")}{" "}
						<span
							className="italic font-medium"
							style={{ color: "#3b0092" }}>
							{t("productPages.airCooled.showcase.titleAccent")}
						</span>
					</>
				}
				description={(() => {
					const company = t(
						"productPages.airCooled.showcase.descriptionLeadCompany",
					);
					const parts = t("productPages.airCooled.showcase.description", {
						company,
					}).split(company);
					return (
						<>
							{parts[0]}
							<span
								className="font-semibold"
								style={{ color: "#3b0092" }}>
								{company}
							</span>
							{parts.slice(1).join(company)}
						</>
					);
				})()}
				images={ACHE_IMAGES}
				imageAlt={t("productPages.airCooled.showcase.imageAlt")}
				wordmarkPrimary="ACHE"
				wordmarkSecondary="POSITRON"
				compliancePill="API 661 Â· ASME VIII"
				captionEyebrow="Fin-Fan Cooler"
				captionTitle="Air Cooled Heat Exchanger"
				eyebrow={t("productPages.airCooled.showcase.eyebrow")}
				specsTitle={t("productPages.airCooled.showcase.specsTitle")}
				specsEyebrow={t("productPages.airCooled.showcase.specsEyebrow")}
				highlights={ACHE_HIGHLIGHTS}
				specs={ACHE_SPECS}
				specsFooterLabel="Positron Engineering Â· ACHE Series"
			/>

			<FooterSection />
		</>
	);
};

export default AirCooledHeatExchangers;
